import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input, InputNumber, Select, Typography } from "antd";
import { STEP_INPUT_POLICIES } from "components/CC/config/stepInputPolicies";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import {
    HTTP_CONVERT_DATA_OPTIONS,
    HTTP_DELIMITER_OPTIONS,
    HTTP_FORMAT_OPTIONS,
    HTTP_METHOD_OPTIONS,
    HTTP_NOTSEND_OPTIONS,
    HTTP_PARSER_OPTIONS,
    HTTP_SIGNATURE_POSITION_OPTIONS,
    HTTP_SIGNATURE_SHA_OPTIONS,
    HTTP_SIGNATURE_TYPE_OPTIONS,
} from "components/CC/Nodes/HttpNode/const";
import { httpFormSchema, type HttpFormData } from "components/CC/Nodes/HttpNode/Forms/formSchema";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { CustomFieldData } from "components/CC/UI/ParameterPicker/parameterUtils";
import { useBuildParameterPickerCategories } from "components/CC/UI/ParameterPicker/useBuildParameterPickerCategories";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import React, { useEffect, useMemo } from "react";
import { Controller, useFieldArray, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";

interface FormProps {
    onChange?: (data: HttpFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
    /** Form fields from API */
    formFields?: App.Data.FormFieldData[];
    /** Custom fields from API (Integration or FormStructure) */
    customFields?: CustomFieldData[];
}

export const HttpForm = ({ onChange, onRegisterValidator, formId, formFields, customFields }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<HttpFormData>
            key={selectedNode?.id}
            id={formId}
            schema={httpFormSchema}
            defaultValues={selectedNode?.formData as HttpFormData}
        >
            <FormContent
                onChange={onChange}
                onRegisterValidator={onRegisterValidator}
                formFields={formFields}
                customFields={customFields}
            />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: HttpFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

const FormContent = ({ onChange, onRegisterValidator, formFields, customFields }: FormContentProps) => {
    const { control, setValue, trigger } = useGeneralForm<HttpFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");
    const { selectedNode } = useSelectedNode();
    const buildCategories = useBuildParameterPickerCategories({
        t,
        formFields,
        customFields,
        currentNodeId: selectedNode?.id,
    });

    const allCategories = useMemo(
        () =>
            buildCategories({
                allowedCategories: [...STEP_INPUT_POLICIES.http.bodyValue.ui.allowedCategories],
            }),
        [buildCategories]
    );
    const headersArray = useFieldArray({ control, name: "headers" });
    const queryArray = useFieldArray({ control, name: "query" });
    const bodyArray = useFieldArray({ control, name: "body" });

    // Watch method to conditionally show body/format/signature sections
    const method = useWatch({ control, name: "method" });
    const methodHasBody = !["GET", "DELETE", "HEAD", "OPTIONS"].includes(method);
    const methodSupportsSignature = !["DELETE", "HEAD", "OPTIONS"].includes(method);

    // Watch signature type to determine which secondary field to show
    const signatureType = useWatch({ control, name: "signature.type" });
    const signature = useWatch({ control, name: "signature" });

    // Determine if current type needs position (MD5) or sha (HMAC/DIGEST)
    const currentTypeConfig = HTTP_SIGNATURE_TYPE_OPTIONS.find((opt) => opt.value === signatureType);
    const showPosition = currentTypeConfig?.hasPosition ?? false;
    const showSha = currentTypeConfig?.hasSha ?? false;

    // Set default sha/position when type changes
    const prevTypeRef = React.useRef(signatureType);
    React.useEffect(() => {
        if (signature && prevTypeRef.current !== signatureType) {
            const newTypeConfig = HTTP_SIGNATURE_TYPE_OPTIONS.find((opt) => opt.value === signatureType);
            if (newTypeConfig?.hasSha && !signature.sha) {
                setValue("signature.sha", "256");
            }
            if (newTypeConfig?.hasPosition && !signature.position) {
                setValue("signature.position", "prefix");
            }
            prevTypeRef.current = signatureType;
        }
    }, [signatureType, signature, setValue]);

    const methodOptions = HTTP_METHOD_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const formatOptions = HTTP_FORMAT_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const parserOptions = HTTP_PARSER_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const signatureTypeOptions = HTTP_SIGNATURE_TYPE_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const signatureShaOptions = HTTP_SIGNATURE_SHA_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const signaturePositionOptions = HTTP_SIGNATURE_POSITION_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const delimiterOptions = HTTP_DELIMITER_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const notsendOptions = HTTP_NOTSEND_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const convertDataOptions = HTTP_CONVERT_DATA_OPTIONS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
    }));

    const handleAddSignature = () => {
        setValue("signature", { type: "md5_values", position: "prefix", delimiter: "|" });
    };

    const handleRemoveSignature = () => {
        setValue("signature", null);
    };

    return (
        <div className="flex flex-col m-3">
            <FormValueSync<HttpFormData> onChange={onChange} />
            {/* Settings Section */}
            <div className="bg-white rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                    <Typography.Title level={4} className="!mb-0">
                        {t("nodes.http.forms.settings")}
                    </Typography.Title>
                    {/* Active toggle — hidden until BE is_enabled is wired up
                    <Controller
                        control={control}
                        name="active"
                        render={({ field }) => (
                            <div className="flex items-center gap-2">
                                <Switch checked={field.value} onChange={field.onChange} />
                                <Typography.Text type="secondary">{t("nodes.http.forms.active")}</Typography.Text>
                            </div>
                        )}
                    /> */}
                </div>

                {/* Title and Subtitle */}
                <div className="flex gap-4 mb-4">
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="title"
                        label={t("nodes.http.forms.title")}
                    >
                        <Input placeholder={t("nodes.http.forms.title_placeholder")} />
                    </FormItem>
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="subtitle"
                        label={t("nodes.http.forms.subtitle")}
                    >
                        <Input placeholder={t("nodes.http.forms.subtitle_placeholder")} />
                    </FormItem>
                </div>

                {/* Method, Format, Timeout */}
                <div className="flex gap-4 mb-4">
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="method"
                        label={t("nodes.http.forms.method")}
                    >
                        <Select options={methodOptions} />
                    </FormItem>
                    {methodHasBody && (
                        <FormItem
                            className="flex-1 !mb-0"
                            control={control}
                            name="format"
                            label={t("nodes.http.forms.format")}
                        >
                            <Select
                                options={formatOptions}
                                allowClear
                                placeholder={t("nodes.http.forms.format_placeholder")}
                            />
                        </FormItem>
                    )}
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="timeout"
                        label={t("nodes.http.forms.timeout")}
                    >
                        <InputNumber className="w-full" min={1} />
                    </FormItem>
                </div>

                {/* Response Format, Blank values don't send, Blank values convert */}
                <div className="flex gap-4 mb-4">
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="parser"
                        label={t("nodes.http.forms.response_format")}
                    >
                        <Select options={parserOptions} />
                    </FormItem>
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="notsend"
                        label={t("nodes.http.forms.notsend")}
                    >
                        <Select
                            options={notsendOptions}
                            allowClear
                            placeholder={t("nodes.http.forms.notsend_placeholder")}
                        />
                    </FormItem>
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="convert_data"
                        label={t("nodes.http.forms.convert_data")}
                    >
                        <Select
                            options={convertDataOptions}
                            allowClear
                            placeholder={t("nodes.http.forms.convert_data_placeholder")}
                        />
                    </FormItem>
                </div>

                {/* Endpoint */}
                <FormItem className="!mb-4" control={control} name="endpoint" label={t("nodes.http.forms.endpoint")}>
                    <TagTextInput
                        placeholder="https://api.example.com"
                        categories={allCategories}
                        mode={STEP_INPUT_POLICIES.http.endpoint.ui.mode}
                    />
                </FormItem>

                {/* Header Section */}
                <DrawerCollapsible
                    title={t("nodes.http.forms.header")}
                    className="!bg-gray-100"
                    wrapperClassName="!p-0 mb-2"
                >
                    {headersArray.fields.length > 0 && (
                        <div className="flex gap-2 mb-2">
                            <Typography.Text type="secondary" className="flex-1">
                                {t("nodes.http.forms.key")}
                            </Typography.Text>
                            <Typography.Text type="secondary" className="flex-1">
                                {t("nodes.http.forms.value")}
                            </Typography.Text>
                            <div className="w-8" />
                        </div>
                    )}
                    {headersArray.fields.map((field, index) => (
                        <div key={field.id} className="flex gap-2 mb-2 items-start">
                            <FormItem className="flex-1 !mb-0" control={control} name={`headers.${index}.key`}>
                                <TagTextInput
                                    placeholder={t("nodes.http.forms.key_placeholder")}
                                    categories={allCategories}
                                    mode={STEP_INPUT_POLICIES.http.headerKey.ui.mode}
                                />
                            </FormItem>
                            <FormItem className="flex-1 !mb-0" control={control} name={`headers.${index}.value`}>
                                <TagTextInput
                                    placeholder={t("nodes.http.forms.value_placeholder")}
                                    categories={allCategories}
                                    mode={STEP_INPUT_POLICIES.http.headerValue.ui.mode}
                                />
                            </FormItem>
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => headersArray.remove(index)}
                            />
                        </div>
                    ))}
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => headersArray.append({ key: "", value: "" })}
                        className="w-full"
                    >
                        {t("nodes.http.forms.add_field")}
                    </Button>
                </DrawerCollapsible>

                {/* Query Section */}
                <DrawerCollapsible
                    title={t("nodes.http.forms.query")}
                    className="!bg-gray-100"
                    wrapperClassName="!p-0 mb-2"
                >
                    {queryArray.fields.length > 0 && (
                        <div className="flex gap-2 mb-2">
                            <Typography.Text type="secondary" className="flex-1">
                                {t("nodes.http.forms.key")}
                            </Typography.Text>
                            <Typography.Text type="secondary" className="flex-1">
                                {t("nodes.http.forms.value")}
                            </Typography.Text>
                            <div className="w-8" />
                        </div>
                    )}
                    {queryArray.fields.map((field, index) => (
                        <div key={field.id} className="flex gap-2 mb-2 items-start">
                            <FormItem className="flex-1 !mb-0" control={control} name={`query.${index}.key`}>
                                <TagTextInput
                                    placeholder={t("nodes.http.forms.key_placeholder")}
                                    categories={allCategories}
                                    mode={STEP_INPUT_POLICIES.http.queryKey.ui.mode}
                                />
                            </FormItem>
                            <FormItem className="flex-1 !mb-0" control={control} name={`query.${index}.value`}>
                                <TagTextInput
                                    placeholder={t("nodes.http.forms.value_placeholder")}
                                    categories={allCategories}
                                    mode={STEP_INPUT_POLICIES.http.queryValue.ui.mode}
                                />
                            </FormItem>
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => queryArray.remove(index)}
                            />
                        </div>
                    ))}
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => queryArray.append({ key: "", value: "" })}
                        className="w-full"
                    >
                        {t("nodes.http.forms.add_field")}
                    </Button>
                </DrawerCollapsible>

                {/* Body Section - only for methods with body */}
                {methodHasBody && (
                    <DrawerCollapsible
                        title={t("nodes.http.forms.body")}
                        className="!bg-gray-100"
                        wrapperClassName="!p-0 mb-2"
                    >
                        {bodyArray.fields.length > 0 && (
                            <div className="flex gap-2 mb-2">
                                <Typography.Text type="secondary" className="flex-1">
                                    {t("nodes.http.forms.key")}
                                </Typography.Text>
                                <Typography.Text type="secondary" className="flex-1">
                                    {t("nodes.http.forms.value")}
                                </Typography.Text>
                                <div className="w-8" />
                            </div>
                        )}
                        {bodyArray.fields.map((field, index) => (
                            <div key={field.id} className="flex gap-2 mb-2 items-start">
                                <FormItem className="flex-1 !mb-0" control={control} name={`body.${index}.key`}>
                                    <TagTextInput
                                        placeholder={t("nodes.http.forms.key_placeholder")}
                                        categories={allCategories}
                                        mode={STEP_INPUT_POLICIES.http.bodyKey.ui.mode}
                                    />
                                </FormItem>
                                <FormItem className="flex-1 !mb-0" control={control} name={`body.${index}.value`}>
                                    <TagTextInput
                                        placeholder={t("nodes.http.forms.value_placeholder")}
                                        categories={allCategories}
                                        mode={STEP_INPUT_POLICIES.http.bodyValue.ui.mode}
                                    />
                                </FormItem>
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => bodyArray.remove(index)}
                                />
                            </div>
                        ))}
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => bodyArray.append({ key: "", value: "" })}
                            className="w-full"
                        >
                            {t("nodes.http.forms.add_field")}
                        </Button>
                    </DrawerCollapsible>
                )}

                {/* Signature Section - for methods that support signatures (GET via query params, POST/PUT/PATCH via body) */}
                {methodSupportsSignature && (
                    <DrawerCollapsible
                        title={t("nodes.http.forms.signature")}
                        className="!bg-gray-100"
                        wrapperClassName="!p-0 mb-2"
                    >
                        {signature ? (
                            <div className="flex gap-2 mb-2 items-start">
                                <FormItem
                                    className="flex-[2] !mb-0"
                                    control={control}
                                    name="signature.type"
                                    label={t("nodes.http.forms.type")}
                                >
                                    <Select options={signatureTypeOptions} />
                                </FormItem>
                                {showPosition && (
                                    <FormItem
                                        className="w-24 !mb-0"
                                        control={control}
                                        name="signature.position"
                                        label={t("nodes.http.forms.position")}
                                    >
                                        <Select options={signaturePositionOptions} />
                                    </FormItem>
                                )}
                                {showSha && (
                                    <FormItem
                                        className="w-24 !mb-0"
                                        control={control}
                                        name="signature.sha"
                                        label={t("nodes.http.forms.sha")}
                                    >
                                        <Select options={signatureShaOptions} />
                                    </FormItem>
                                )}
                                <FormItem
                                    className="w-20 !mb-0"
                                    control={control}
                                    name="signature.delimiter"
                                    label={t("nodes.http.forms.delimiter")}
                                >
                                    <Select options={delimiterOptions} />
                                </FormItem>
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleRemoveSignature}
                                    className="mt-[30px]"
                                />
                            </div>
                        ) : (
                            <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={handleAddSignature}
                                className="w-full"
                            >
                                {t("nodes.http.forms.add_signature")}
                            </Button>
                        )}
                    </DrawerCollapsible>
                )}

                {/* Advanced Settings Section */}
                <DrawerCollapsible
                    title={t("nodes.http.forms.advanced_settings")}
                    className="!bg-gray-100"
                    wrapperClassName="!p-0"
                    isDefaultOpen={true}
                >
                    <Controller
                        control={control}
                        name="insecure"
                        render={({ field }) => (
                            <Checkbox
                                checked={!!field.value}
                                onChange={(e) => field.onChange(e.target.checked || null)}
                            >
                                {t("nodes.http.forms.insecure")}
                            </Checkbox>
                        )}
                    />
                </DrawerCollapsible>
            </div>
        </div>
    );
};
